import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";

import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { getUserPerks } from "../../services/userPerksService";
import { fetchFanProfileStats } from "../../services/fanProfileService";
import { followingIdsFromUser, isFollowingListPrivate } from "../../services/followService";
import FollowingOverlay, { FollowingStatPill } from "../../components/profile/FollowingOverlay";
import {
  LOGGED_OUT_SIGNIN_PATH,
  MaybeLoggedOutLockedSection,
  loggedOutSignInProps,
} from "../../components/profile/LoggedOutProfileGate";
import { getNicheMeta } from "../../components/niche/nicheMeta";
import { hasCreatorAccess } from "../../utils/roleUtils";
import { getFanProfileEditPath, getUserProfilePath } from "../../utils/profilePaths";
import AvatarWithTrophy from "../../components/profile/AvatarWithTrophy";
import ActivityTab from "../../components/profile/ActivityTab";
import PrivateCollectionTab from "../../components/profile/PrivateCollectionTab";
import TrophyCaseTab from "../../components/profile/TrophyCaseTab";
import { getEarnedTrophies } from "../../data/trophyCatalog";
import {
  displayProfileThemeId,
  getProfileThemeStyle,
} from "../../constants/profileThemeCatalog";
import { formatCount } from "../../utils/formatCount";
import { setPageTitle } from "../../components/DocumentTitle";
import PipAnchor from "../../components/Pip/PipAnchor";
import PipPageTip from "../../components/Pip/PipPageTip";

import "../../screens/UserProfile.css";
import "../../Styles/profileThemes.css";

const STROKE = 1.8;

async function fetchUserById(userId) {
  const snap = await getDoc(doc(db, "users", userId));
  if (!snap.exists()) return null;
  return { uid: snap.id, ...snap.data() };
}

export default function FanProfile() {
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile, userPerks, refreshUserPerks, refreshUserProfile } = useAuth();

  const [profileUser, setProfileUser] = useState(null);
  const [profilePerks, setProfilePerks] = useState(null);
  const [profileStats, setProfileStats] = useState(null);
  const [followedCreators, setFollowedCreators] = useState([]);
  const [activeTab, setActiveTab] = useState("activity");
  const [loading, setLoading] = useState(true);
  const [followingOpen, setFollowingOpen] = useState(false);

  const targetUserId = paramUserId;
  const isOwnProfile = !!(currentUser && targetUserId === currentUser.uid);
  const isLoggedOut = !currentUser;

  // Keep Auth credits fresh when viewing your own profile (matches Perks)
  useEffect(() => {
    if (isOwnProfile && currentUser?.uid) {
      refreshUserPerks(currentUser.uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh only on profile identity change
  }, [isOwnProfile, currentUser?.uid]);

  // Own profile: stay in sync with Auth (same source Perks reads)
  useEffect(() => {
    if (isOwnProfile && userPerks) {
      setProfilePerks(userPerks);
    }
  }, [isOwnProfile, userPerks]);

  useEffect(() => {
    if (!targetUserId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = isOwnProfile && userProfile
          ? { uid: currentUser.uid, ...userProfile }
          : await fetchUserById(targetUserId);

        if (cancelled) return;
        setProfileUser(data);

        if (data?.uid) {
          try {
            const [perks, stats] = await Promise.all([
              getUserPerks(data.uid).catch(() => null),
              fetchFanProfileStats(data.uid).catch(() => ({ following: 0, unlocked: 0, firstViews: 0 })),
            ]);
            if (!cancelled) {
              setProfilePerks(
                isOwnProfile && userPerks ? userPerks : perks
              );
              setProfileStats(stats);
            }
          } catch (err) {
            console.warn("FanProfile extras:", err);
          }
        }

        try {
          if (data?.uid && (isOwnProfile || data.followingListPrivate !== true)) {
            const followSnap = await getDocs(
              query(collection(db, "follows"), where("followerId", "==", data.uid))
            );
            const creatorIds = followSnap.docs.map((d) => d.data().creatorId).filter(Boolean).slice(0, 6);
            if (creatorIds.length > 0) {
              const creators = await Promise.all(
                creatorIds.map(async (id) => {
                  const cSnap = await getDoc(doc(db, "users", id));
                  return cSnap.exists() ? { uid: id, ...cSnap.data() } : null;
                })
              );
              if (!cancelled) setFollowedCreators(creators.filter(Boolean));
            } else if (!cancelled) {
              setFollowedCreators([]);
            }
          } else if (!cancelled) {
            setFollowedCreators([]);
          }
        } catch (err) {
          console.warn("FanProfile following list:", err);
          if (!cancelled) setFollowedCreators([]);
        }
      } catch (err) {
        console.error("FanProfile load error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [targetUserId, currentUser, userProfile, isOwnProfile, userPerks]);

  useEffect(() => {
    if (!profileUser) return undefined;
    const name = profileUser.displayName || profileUser.username || "Profile";
    setPageTitle(`100ViewsOnly — ${name}`);
    return undefined;
  }, [profileUser]);

  if (loading) {
    return <div className="up-page up-page--loading">Loading profile…</div>;
  }

  if (!profileUser) {
    return (
      <div className="up-page up-page--empty">
        <h1>Profile not found</h1>
        <Link to="/gallery" className="up-btn up-btn--gold">Browse creators</Link>
      </div>
    );
  }

  if (hasCreatorAccess(profileUser) && !isOwnProfile) {
    const creatorPath = getUserProfilePath(profileUser);
    if (creatorPath?.startsWith("/creator/")) {
      return <Navigate to={creatorPath} replace />;
    }
  }

  const handle = profileUser.handle || profileUser.username || profileUser.uid;
  // Own profile: Auth userPerks is the same source Perks uses
  const credits = isOwnProfile
    ? (userPerks?.credits ?? profilePerks?.credits ?? 0)
    : null;
  const streak = (isOwnProfile ? userPerks?.streak : profilePerks?.streak) ?? profilePerks?.streak ?? 0;
  const collectionPrivate = profileUser.privacy?.privateCollection !== "public" && !isOwnProfile;
  const collectionPublic = profileUser.privacy?.privateCollection === "public";
  const trophyPrivate = profileUser.privacy?.trophyCase === "private" && !isOwnProfile;
  const earnedTrophies = getEarnedTrophies(profileUser);
  const editPath = getFanProfileEditPath(profileUser.uid);

  const stats = profileStats || { following: 0, unlocked: 0, firstViews: 0 };

  const displayThemeId = displayProfileThemeId(profileUser.profileTheme, profileUser);
  const profileThemeStyle = displayThemeId ? getProfileThemeStyle(displayThemeId) : undefined;
  const fanFirst = profileUser.displayName?.split(" ")[0] || "this fan";
  const signInHotspot = loggedOutSignInProps(isLoggedOut, navigate);

  return (
    <div
      className="up-page"
      data-profile-theme={displayThemeId || undefined}
      style={profileThemeStyle}
    >
      <header className="up-header">
        <div className="up-header-inner">
          <div
            className={`up-av-wrap${isLoggedOut ? " up-signin-hotspot" : ""}`}
            {...(isLoggedOut ? signInHotspot : {})}
          >
            <AvatarWithTrophy
              src={profileUser.avatar || profileUser.avatarUrl}
              alt={profileUser.displayName || handle}
              imgClassName="up-av"
              size="lg"
              pinnedTrophy={profileUser.pinnedTrophy}
            />
            {isOwnProfile && (
              <div className="up-credits-badge">
                <span className="up-cb-num">{formatCount(credits ?? 0)}</span>
                <span className="up-cb-lbl">Credits</span>
              </div>
            )}
          </div>

          <div className="up-meta">
            <h1 className="up-name">{profileUser.displayName || handle}</h1>
            <div className="up-handle-row">
              <span>@{handle}</span>
              <span className="up-badge up-badge--fan">Fan</span>
              {profilePerks?.isFoundingMember && (
                <span className="up-badge up-badge--early">Early Member</span>
              )}
            </div>
            {profileUser.bio && <p className="up-bio">{profileUser.bio}</p>}
            {(profileUser.interests || profileUser.tags)?.length > 0 && (
              <div className="up-interests">
                {(profileUser.interests || profileUser.tags).map((tag) => (
                  <span key={tag} className="up-ui">{tag}</span>
                ))}
              </div>
            )}
            {earnedTrophies.length > 0 && (
              <div className="up-trophy-preview">
                <div className="up-trophy-preview-icons">
                  {earnedTrophies.slice(0, 4).map((t) => (
                    <span key={t.key} className="up-trophy-preview-icon" title={t.name}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth={STROKE} strokeLinecap="round">
                        <path d="M6 9H4.5a2.5 2.5 0 010-5H6" /><path d="M18 9h1.5a2.5 2.5 0 000-5H18" />
                        <path d="M4 22h16" /><path d="M18 2H6v7a6 6 0 0012 0V2z" />
                      </svg>
                    </span>
                  ))}
                  {earnedTrophies.length > 4 && (
                    <span className="up-trophy-preview-more">+{earnedTrophies.length - 4}</span>
                  )}
                </div>
                <button
                  type="button"
                  className="up-trophy-preview-link"
                  onClick={() => (isLoggedOut ? navigate(LOGGED_OUT_SIGNIN_PATH) : setActiveTab("trophies"))}
                >
                  View Trophy Case →
                </button>
              </div>
            )}
          </div>

          <div className="up-stats-col">
            <div className="up-stats-row">
              {isOwnProfile ? (
                <>
                  <PipAnchor id="onboard-following" style={{ display: "inline-flex" }}>
                    <FollowingStatPill
                      count={followingIdsFromUser(profileUser).length}
                      isPrivate={isFollowingListPrivate(profileUser)}
                      onClick={() => setFollowingOpen(true)}
                    />
                  </PipAnchor>
                  <PipPageTip
                    anchorId="onboard-following"
                    flag="seenCollectionTip"
                    text="Following is your collection — everyone you follow lives here."
                  />
                </>
              ) : (
                <FollowingStatPill
                  count={followingIdsFromUser(profileUser).length}
                  isPrivate={isFollowingListPrivate(profileUser)}
                  onClick={() => (isLoggedOut ? navigate(LOGGED_OUT_SIGNIN_PATH) : setFollowingOpen(true))}
                />
              )}
              <div className="up-stat">
                <div className="up-stat-num">{stats.unlocked + stats.firstViews}</div>
                <div className="up-stat-lbl">Unlocked</div>
              </div>
              <div className="up-stat">
                <div className="up-stat-num">{stats.firstViews}</div>
                <div className="up-stat-lbl">First Views</div>
              </div>
              {isOwnProfile && (
                <div className="up-stat">
                  <div className="up-stat-num">{formatCount(credits ?? 0)}</div>
                  <div className="up-stat-lbl">Credits</div>
                </div>
              )}
            </div>
            {isOwnProfile && (
              <Link to={editPath} className="up-btn-edit">Edit Profile</Link>
            )}
            {isLoggedOut && (
              <button
                type="button"
                className="up-btn-edit up-btn-follow"
                onClick={() => navigate(LOGGED_OUT_SIGNIN_PATH)}
              >
                Follow
              </button>
            )}
          </div>
        </div>
      </header>

      <MaybeLoggedOutLockedSection locked={isLoggedOut} name={fanFirst} noun="collection">
        <div className="up-tabs-wrap up-tabs-wrap-sticky">
          <div className="up-tabs">
            <button
              type="button"
              className={`up-tab${activeTab === "activity" ? " up-tab--active" : ""}`}
              onClick={() => setActiveTab("activity")}
            >
              Activity
            </button>
            <button
              type="button"
              className={`up-tab${activeTab === "collection" ? " up-tab--active" : ""}`}
              onClick={() => setActiveTab("collection")}
            >
              Collection
            </button>
            <button
              type="button"
              className={`up-tab${activeTab === "trophies" ? " up-tab--active" : ""}`}
              onClick={() => setActiveTab("trophies")}
            >
              Trophies
              {earnedTrophies.length > 0 && (
                <span className="up-tab-badge">{earnedTrophies.length}</span>
              )}
            </button>
          </div>
        </div>

      <div className="up-body">
        <main className="up-main">
          {activeTab === "activity" && (
            <ActivityTab userId={profileUser.uid} />
          )}

          {activeTab === "collection" && (
            collectionPrivate ? (
              <div className="up-pc-locked">
                <div className="up-pc-locked-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </div>
                <h3>This collection is private</h3>
                <p>Only {profileUser.displayName || handle} can see their saved and unlocked drops.</p>
              </div>
            ) : (
              <PrivateCollectionTab
                userId={profileUser.uid}
                isOwnProfile={isOwnProfile}
                isPublicView={collectionPublic && !isOwnProfile}
              />
            )
          )}

          {activeTab === "trophies" && (
            trophyPrivate ? (
              <div className="up-pc-locked">
                <div className="up-pc-locked-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                </div>
                <h3>This trophy case is private</h3>
                <p>{profileUser.displayName || handle} has chosen to keep their trophies private.</p>
              </div>
            ) : (
              <TrophyCaseTab
                profileUser={profileUser}
                isOwnProfile={isOwnProfile}
                avatarUrl={profileUser.avatar || profileUser.avatarUrl}
                editPath={editPath}
              />
            )
          )}
        </main>

        <aside className="up-sidebar">
          {isOwnProfile && (
            <div className="up-credits-card">
              <div className="up-cc-label"><span className="up-cc-dot" /> Your balance</div>
              <div className="up-cc-num">{formatCount(credits ?? 0)}</div>
              <div className="up-cc-lbl">credits available</div>
              <span className="up-cc-hand">spend them in Perks</span>
              <div className="up-cc-rule" />
              <Link to="/perks" className="up-cc-link">Go to Perks →</Link>
            </div>
          )}

          {followedCreators.length > 0 && (isOwnProfile || !isFollowingListPrivate(profileUser)) && (
            <div className="up-following-card">
              <div className="up-fc-label">
                Following
                {isOwnProfile && <Link to="/following">View all</Link>}
              </div>
              <div className="up-fc-list">
                {followedCreators.slice(0, 4).map((creator) => {
                  const cHandle = creator.handle || creator.username || creator.uid;
                  return (
                    <Link key={creator.uid} to={`/creator/${cHandle}`} className="up-fc-row">
                      <AvatarWithTrophy
                        src={creator.avatar || creator.avatarUrl}
                        alt={creator.displayName || cHandle}
                        imgClassName="up-fc-av-sm"
                        size="sm"
                        pinnedTrophy={creator.pinnedTrophy}
                      />
                      <div className="up-fc-info">
                        <div className="up-fc-name">{creator.displayName || cHandle}</div>
                        <div className="up-fc-niche">{getNicheMeta(creator.niche).label}</div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {isOwnProfile && streak > 0 && (
            <div className="up-streak-card">
              <div className="up-sk-label">Daily streak</div>
              <div className="up-sk-row">
                <div className="up-sk-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={STROKE} strokeLinecap="round">
                    <path d="M12 2c1 3 2.5 3.5 3.5 4.5A5 5 0 0117 10c0 3.5-2.5 6-5 8-2.5-2-5-4.5-5-8a5 5 0 011.5-3.5C9.5 5.5 11 5 12 2z" />
                  </svg>
                </div>
                <div className="up-sk-body">
                  <div className="up-sk-num">{streak}</div>
                  <div className="up-sk-title">days in a row</div>
                  <div className="up-sk-sub">Keep visiting to earn bonus credits</div>
                </div>
              </div>
              <span className="up-sk-hand">don&apos;t break the chain!</span>
            </div>
          )}
        </aside>
      </div>
      </MaybeLoggedOutLockedSection>

      {currentUser ? (
        <FollowingOverlay
          open={followingOpen}
          onClose={() => setFollowingOpen(false)}
          profileUser={profileUser}
          isOwner={isOwnProfile}
          currentUser={userProfile || { uid: currentUser.uid }}
          onPrivacyChange={(value) => {
            setProfileUser((prev) => (prev ? { ...prev, followingListPrivate: value } : prev));
            if (isOwnProfile && currentUser?.uid) refreshUserProfile?.(currentUser.uid);
          }}
          onOwnerUnfollow={(id) => {
            setProfileUser((prev) => {
              if (!prev) return prev;
              return { ...prev, following: followingIdsFromUser(prev).filter((x) => x !== id) };
            });
            setFollowedCreators((prev) => prev.filter((c) => c.uid !== id));
            if (isOwnProfile && currentUser?.uid) refreshUserProfile?.(currentUser.uid);
          }}
        />
      ) : null}
    </div>
  );
}
